<template>
    <ToolTip
        v-if="truncateLines > 0"
        :content="productName"
        :disabled="!isTruncated"
        class-name="product-name-truncate-tooltip"
    >
        <component
            :is="linkUrl ? 'router-link' : 'span'"
            ref="nameEl"
            :to="linkUrl || undefined"
            class="product-name-truncate"
            :style="{ '--product-name-lines': truncateLines }"
        >{{ productName }}</component>
    </ToolTip>
    <template v-else-if="linkUrl">
        <router-link :to="linkUrl">{{ productName }}</router-link>
    </template>
    <template v-else>{{ productName }}</template>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import ToolTip from './ToolTip.vue';

const props = defineProps({
    product: {
        type: Object,
        required: true
    },
    deliveryDate: {
        type: String,
        required: false,
        default: ''
    },
    quoteDate: {
        type: String,
        required: false,
        default: '',
    },
    link: {
        type: Boolean,
        required: false,
        default: true
    },
    color: {
        type: Boolean,
        required: false,
        default: true
    },
    truncateLines: {
        type: Number,
        default: 0,
    },
});

const excludedPdpLinkProgramCodes = [
    'dbl', 
    'dbl2', 
    'mmt',
    'sbl'
];

const nameEl = ref(null);
const isTruncated = ref(false);
let resizeObserver = null;

const productName = computed(() => {
    let note = '';
    if (props.product.notes != null && props.product.notes != '') {
        note = ' ' + props.product.notes;
    }

    if (props.product.programtype == 'hgs') {
        return props.product.name + note;
    }

    let name = '';
    if (props.product.product_name != null && props.product.product_name != '') {
        name = props.product.product_name;
    }

    if (!props.color && props.product.name_no_color != null && props.product.name_no_color != '') {
        name = props.product.name_no_color;
    }
    else if (props.product.name != null && props.product.name != '') {
        name = props.product.name;
    }

    return name + note;
});

const linkUrl = computed(() => {
    let programCode = props.product.program_code || props.product.programtype || '';
    programCode = programCode.toLowerCase();
    if (!excludedPdpLinkProgramCodes.includes(programCode) && props.link) {
        if (props.quoteDate) {
            const quoteDate = String(props.quoteDate).split('T')[0].split(' ')[0];
            return '/' + props.product.slug + '?quote_date=' + quoteDate;
        }
        let deliveryDate = '';
        if (props.deliveryDate) {
            deliveryDate = '?'+programCode+'_delivery_date=' + props.deliveryDate;
        }
        return '/' + props.product.slug + deliveryDate;
    }
    return null;
});

const getNameNode = () => {
    const el = nameEl.value;
    if (!el) return null;
    return el instanceof HTMLElement ? el : el.$el;
};

const checkTruncation = () => {
    const el = getNameNode();
    if (!el || props.truncateLines <= 0) {
        isTruncated.value = false;
        return;
    }
    isTruncated.value = el.scrollHeight > el.clientHeight + 1;
};

const scheduleTruncationCheck = () => {
    nextTick(checkTruncation);
};

watch(
    () => [productName.value, props.truncateLines, linkUrl.value],
    () => {
        if (props.truncateLines > 0) {
            scheduleTruncationCheck();
        } else {
            isTruncated.value = false;
        }
    },
);

onMounted(() => {
    if (props.truncateLines <= 0) return;

    const observe = () => {
        const el = getNameNode();
        if (!el || typeof ResizeObserver === 'undefined') return;
        resizeObserver?.disconnect();
        resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(el);
        checkTruncation();
    };

    nextTick(observe);
    window.addEventListener('resize', checkTruncation);
});

onUnmounted(() => {
    resizeObserver?.disconnect();
    window.removeEventListener('resize', checkTruncation);
});
</script>

<style scoped>
.product-name-truncate {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--product-name-lines, 2);
    overflow: hidden;
    overflow-wrap: break-word;
    word-break: break-word;
    min-width: 0;
    width: 100%;
}

:deep(.product-name-truncate-tooltip.tooltip-wrap),
:deep(.product-name-truncate-tooltip) {
    display: block;
    width: 100%;
    min-width: 0;
    max-width: 100%;
}
</style>
